// src/pages/analytics/OrderSheetTab.tsx
//
// Order Sheet — line-level open/invoiced order browser (recipe: orders-sheet).
// Filter bar (date range / customer / item / category / status pills),
// 4 summary tiles, paginated table (server-side offset paging), status
// badges, CSV export of the current page.
//
// Dates from the recipe are plain yyyy-MM-dd strings — rendered as-is,
// never through new Date() (agent timestamps lack 'Z'; these aren't even
// timestamps).

import { useState } from 'react';
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import {
  ClipboardList, RefreshCw, Download, ChevronLeft, ChevronRight, AlertTriangle,
  FileSpreadsheet, RotateCcw,
} from 'lucide-react';
import { apiV2 as api } from '../../services/apiV2';
import DateField from '../../components/DateField';
import type { OrderSheetLine, OrderSheetStatus } from '../../types';
import { exportCsv, type CsvColumns } from '../../lib/csvExport';
import { exportXlsx } from '../../lib/xlsxExport';
import { FilterSelect, FilterText } from './salesFilterBar';
import { subcategoryOptions } from './itemSubcategories';

const PAGE_SIZE = 100;

const fmtCur = (v: number) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(v);
const fmtNum = (v: number) => new Intl.NumberFormat('en-US').format(Math.round(v));

const todayStr = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`; };
const daysAgoStr = (days: number) => { const d = new Date(); d.setDate(d.getDate() - days); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`; };

const STATUS_PILLS: { value: OrderSheetStatus; label: string }[] = [
  { value: 'all',      label: 'All' },
  { value: 'open',     label: 'Open' },
  { value: 'invoiced', label: 'Invoiced' },
  { value: 'onhold',   label: 'On Hold' },
  { value: 'void',     label: 'Void' },
];

// Line status is derived: void wins, then on-hold, then invoiced, else open.
function lineStatus(l: OrderSheetLine): { label: string; cls: string } {
  if (l.voided)   return { label: 'Void',     cls: 'bg-red-500/15 text-red-300 border-red-500/30' };
  if (l.onhold)   return { label: 'On Hold',  cls: 'bg-amber-500/15 text-amber-300 border-amber-500/30' };
  if (l.invoiced) return { label: 'Invoiced', cls: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30' };
  return { label: 'Open', cls: 'bg-blue-500/15 text-blue-300 border-blue-500/30' };
}

function orderCols(): CsvColumns<OrderSheetLine> {
  return [
    ['Order #',      (r) => r.orderNo],
    ['Order Date',   (r) => r.orderDate],
    ['Req Date',     (r) => r.reqDate],
    ['Customer',     (r) => r.customerCode],
    ['Customer Name',(r) => r.customerName],
    ['Line',         (r) => r.lineNo],
    ['Item',         (r) => r.itemNo],
    ['Description',  (r) => r.description],
    ['Category',     (r) => r.category],
    ['UM',           (r) => r.um],
    ['Qty',          (r) => r.quantity],
    ['Shipped',      (r) => r.shipped],
    ['Price',        (r) => r.price ?? ''],
    ['Ext',          (r) => r.ext ?? ''],
    ['Status',       (r) => lineStatus(r).label],
    ['Order Total',  (r) => r.orderTotal],
  ];
}
const exportOrdersCsv  = (rows: OrderSheetLine[]) => exportCsv('order-sheet', orderCols(), rows);
const exportOrdersXlsx = (rows: OrderSheetLine[]) => exportXlsx('order-sheet', orderCols(), rows);

// Initial filter state — the Reset button returns every control to these.
const INITIAL_ORDER_FILTERS = {
  dateFrom: daysAgoStr(90),
  dateTo:   todayStr(),
  customer: '',
  item:     '',
  category: '',
  subcategory: '',
  status:   'all' as OrderSheetStatus,
};

export function OrderSheetTab() {
  const [dateFrom, setDateFrom] = useState(INITIAL_ORDER_FILTERS.dateFrom);
  const [dateTo, setDateTo] = useState(INITIAL_ORDER_FILTERS.dateTo);
  const [customer, setCustomer] = useState(INITIAL_ORDER_FILTERS.customer);   // numeric code OR name substring
  const [item, setItem] = useState(INITIAL_ORDER_FILTERS.item);               // itemNo or description substring
  const [category, setCategory] = useState(INITIAL_ORDER_FILTERS.category);
  const [subcategory, setSubcategory] = useState(INITIAL_ORDER_FILTERS.subcategory);
  const [status, setStatus] = useState<OrderSheetStatus>(INITIAL_ORDER_FILTERS.status);
  const [offset, setOffset] = useState(0);

  // Any filter change restarts paging at 0.
  const withReset = <T,>(setter: (v: T) => void) => (v: T) => { setOffset(0); setter(v); };

  // Reset every control to its initial default.
  const resetFilters = () => {
    setDateFrom(INITIAL_ORDER_FILTERS.dateFrom);
    setDateTo(INITIAL_ORDER_FILTERS.dateTo);
    setCustomer(INITIAL_ORDER_FILTERS.customer);
    setItem(INITIAL_ORDER_FILTERS.item);
    setCategory(INITIAL_ORDER_FILTERS.category);
    setSubcategory(INITIAL_ORDER_FILTERS.subcategory);
    setStatus(INITIAL_ORDER_FILTERS.status);
    setOffset(0);
  };

  const q = useQuery({
    queryKey: ['orders-sheet', dateFrom, dateTo, customer, item, category, subcategory, status, offset],
    queryFn: () => api.getOrdersSheet({
      dateFrom: dateFrom || undefined,
      dateTo:   dateTo || undefined,
      customer: customer || undefined,
      item:     item || undefined,
      category: category || undefined,
      itemSubcategory: subcategory || undefined,
      status:   status !== 'all' ? status : undefined,
      limit:    String(PAGE_SIZE),
      offset:   String(offset),
    }),
    placeholderData: keepPreviousData,
    staleTime: 60_000,
    retry: 1,
  });

  // Subcategory list comes from analytics-filters (agent 3.21.0+); hidden on older agents.
  const { data: filters } = useQuery({
    queryKey: ['v2-analyticsFilters'],
    queryFn: () => api.getAnalyticsFilters(),
    staleTime: 5 * 60 * 1000,
  });
  const subcats = subcategoryOptions(filters, category);

  const data = q.data;
  const rows = data?.lines ?? [];
  const returned = data?.page.returned ?? 0;
  const hasNext = returned === (data?.page.limit ?? PAGE_SIZE);

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-xl font-semibold text-white flex items-center gap-2">
          <ClipboardList size={20} /> Order Sheet
        </h2>
        <p className="text-sm text-slate-400 mt-0.5">
          Line-level order browser — open, invoiced, and on-hold order lines with pricing.
        </p>
      </div>

      {/* Filter bar */}
      <div className="flex flex-wrap items-end gap-3 p-4 rounded-xl bg-slate-800/20 border border-slate-700/30">
        <div className="flex flex-col gap-1">
          <span className="text-xs font-medium text-slate-400 uppercase tracking-wider">Order Date</span>
          <div className="flex items-center gap-1.5">
            <DateField value={dateFrom} onChange={withReset(setDateFrom)} className="w-[130px]" />
            <span className="text-slate-600 text-xs">→</span>
            <DateField value={dateTo} onChange={withReset(setDateTo)} className="w-[130px]" />
          </div>
        </div>
        <FilterText label="Customer" value={customer} onChange={withReset(setCustomer)} placeholder="Code or name…" />
        <FilterText label="Item" value={item} onChange={withReset(setItem)} placeholder="Item # or description…" />
        <FilterSelect label="Category" value={category} onChange={withReset((v: string) => { setCategory(v); setSubcategory(''); })}
          options={(data?.facets.categories ?? []).map(c => ({ value: c, label: c }))} />
        {subcats && (
          <FilterSelect label="Subcategory" value={subcategory} onChange={withReset(setSubcategory)}
            options={subcats.map(c => ({ value: c, label: c }))} />
        )}
        <div className="flex flex-col gap-1">
          <span className="text-xs font-medium text-slate-400 uppercase tracking-wider">Status</span>
          <div className="flex items-center gap-1.5">
            {STATUS_PILLS.map(p => (
              <button key={p.value} onClick={() => withReset(setStatus)(p.value)}
                className={`px-2 py-1.5 text-[11px] rounded-md border transition-all ${
                  status === p.value
                    ? 'bg-blue-500/15 text-blue-200 border-blue-500/40'
                    : 'border-slate-700/50 text-slate-500 hover:text-slate-300 bg-slate-900/40'
                }`}>
                {p.label}
              </button>
            ))}
          </div>
        </div>
        <div className="flex-1" />
        <button
          onClick={resetFilters}
          title="Reset all filters to defaults"
          className="px-2 py-1.5 text-[11px] rounded-md border border-slate-700/50 text-slate-300 hover:text-white hover:border-slate-500 bg-slate-900/40 flex items-center gap-1">
          <RotateCcw size={11} /> Reset
        </button>
        <button
          onClick={() => exportOrdersCsv(rows)}
          disabled={rows.length === 0}
          title="Export the current page to CSV"
          className="px-2 py-1.5 text-[11px] rounded-md border border-slate-700/50 text-slate-300 hover:text-white hover:border-slate-500 bg-slate-900/40 flex items-center gap-1 disabled:opacity-40">
          <Download size={11} /> CSV
        </button>
        <button
          onClick={() => exportOrdersXlsx(rows)}
          disabled={rows.length === 0}
          title="Export the current page to Excel"
          className="px-2 py-1.5 text-[11px] rounded-md border border-slate-700/50 text-slate-300 hover:text-white hover:border-slate-500 bg-slate-900/40 flex items-center gap-1 disabled:opacity-40">
          <FileSpreadsheet size={11} /> Excel
        </button>
        <button onClick={() => q.refetch()} disabled={q.isLoading}
          className="p-2 rounded-md bg-slate-700/50 border border-slate-600/50 text-slate-400 hover:text-white transition-all disabled:opacity-50">
          <RefreshCw size={14} className={q.isFetching ? 'animate-spin' : ''} />
        </button>
      </div>

      {/* Summary tiles */}
      {data && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div className="p-3 rounded-lg bg-slate-800/30 border border-slate-700/30">
            <p className="text-xs text-slate-500">Orders</p><p className="text-lg font-bold text-white">{fmtNum(data.summary.orderCount)}</p>
          </div>
          <div className="p-3 rounded-lg bg-slate-800/30 border border-slate-700/30">
            <p className="text-xs text-slate-500">Lines</p><p className="text-lg font-bold text-white">{fmtNum(data.summary.lineCount)}</p>
          </div>
          <div className="p-3 rounded-lg bg-slate-800/30 border border-slate-700/30">
            <p className="text-xs text-slate-500">Total Qty</p><p className="text-lg font-bold text-white">{fmtNum(data.summary.totalQty)}</p>
          </div>
          <div className="p-3 rounded-lg bg-slate-800/30 border border-slate-700/30">
            <p className="text-xs text-slate-500">Total $</p><p className="text-lg font-bold text-white">{fmtCur(data.summary.totalExt)}</p>
          </div>
        </div>
      )}

      {q.error && (
        <div className="p-4 rounded-md bg-red-950/30 border border-red-900/40 text-red-200 text-sm flex items-center gap-2">
          <AlertTriangle size={16} />
          <div>
            <div className="font-medium">Couldn't load orders</div>
            <div className="text-xs text-red-300/70 mt-0.5">{(q.error as Error).message}</div>
          </div>
        </div>
      )}

      {/* Table */}
      {q.isLoading ? (
        <div className="flex items-center justify-center h-40"><RefreshCw className="w-6 h-6 text-blue-400 animate-spin" /></div>
      ) : !q.error && (
        <div className="rounded-xl border border-slate-700/50 bg-slate-800/30 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b border-slate-700/50 bg-slate-800/50">
                  {/* Sticky identifying column — opaque bg so scrolled content doesn't show through */}
                  <th className="sticky left-0 z-10 bg-slate-800 px-3 py-2.5 text-left text-slate-400 font-medium">Order #</th>
                  <th className="px-3 py-2.5 text-left text-slate-400 font-medium">Date</th>
                  <th className="px-3 py-2.5 text-left text-slate-400 font-medium">Req Date</th>
                  <th className="px-3 py-2.5 text-left text-slate-400 font-medium">Customer</th>
                  <th className="px-3 py-2.5 text-left text-slate-400 font-medium">Item</th>
                  <th className="px-3 py-2.5 text-left text-slate-400 font-medium">Description</th>
                  <th className="px-3 py-2.5 text-left text-slate-400 font-medium">Category</th>
                  <th className="px-3 py-2.5 text-left text-slate-400 font-medium">UM</th>
                  <th className="px-3 py-2.5 text-right text-slate-400 font-medium">Qty</th>
                  <th className="px-3 py-2.5 text-right text-slate-400 font-medium">Shipped</th>
                  <th className="px-3 py-2.5 text-right text-slate-400 font-medium">Price</th>
                  <th className="px-3 py-2.5 text-right text-slate-400 font-medium">Ext</th>
                  <th className="px-3 py-2.5 text-left text-slate-400 font-medium">Status</th>
                  <th className="px-3 py-2.5 text-right text-slate-400 font-medium">Order Total</th>
                </tr>
              </thead>
              <tbody>
                {rows.map(l => {
                  const st = lineStatus(l);
                  return (
                    <tr key={`${l.orderNo}-${l.lineNo}-${l.itemNo}`} className="group border-b border-slate-800/50 hover:bg-slate-700/20 transition-colors">
                      <td className="sticky left-0 z-10 bg-slate-900 group-hover:bg-slate-800 px-3 py-2 font-mono text-slate-300">{l.orderNo}</td>
                      <td className="px-3 py-2 text-slate-400 whitespace-nowrap">{l.orderDate}</td>
                      <td className="px-3 py-2 text-slate-400 whitespace-nowrap">{l.reqDate}</td>
                      <td className="px-3 py-2 text-white max-w-[180px] truncate" title={`${l.customerCode} - ${l.customerName}`}>
                        <span className="font-mono text-slate-500 mr-1.5">{l.customerCode}</span>{l.customerName}
                      </td>
                      <td className="px-3 py-2 font-mono text-slate-300">{l.itemNo}</td>
                      <td className="px-3 py-2 text-white max-w-[200px] truncate">{l.description}</td>
                      <td className="px-3 py-2 text-slate-400">{l.category}</td>
                      <td className="px-3 py-2 text-slate-400">{l.um}</td>
                      <td className="px-3 py-2 text-right text-slate-300">{fmtNum(l.quantity)}</td>
                      <td className="px-3 py-2 text-right text-slate-400">{fmtNum(l.shipped)}</td>
                      <td className="px-3 py-2 text-right text-slate-300">{l.price != null ? fmtCur(l.price) : '—'}</td>
                      <td className="px-3 py-2 text-right text-slate-300">{l.ext != null ? fmtCur(l.ext) : '—'}</td>
                      <td className="px-3 py-2"><span className={`text-[10px] px-1.5 py-0.5 rounded border ${st.cls}`}>{st.label}</span></td>
                      <td className="px-3 py-2 text-right text-slate-400">{fmtCur(l.orderTotal)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {rows.length === 0 && (
            <div className="text-center py-8 text-slate-500 text-sm">No order lines match the selected filters</div>
          )}
          {/* Pager */}
          <div className="px-3 py-2 border-t border-slate-700/30 flex items-center justify-between gap-3">
            <div className="text-xs text-slate-500">
              {returned > 0 ? `Showing ${offset + 1}–${offset + returned}` : 'No rows'}
              {q.isFetching && !q.isLoading && ' · refreshing…'}
            </div>
            <div className="flex items-center gap-1.5">
              <button
                onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
                disabled={offset === 0 || q.isFetching}
                className="px-3 py-2.5 md:px-2 md:py-1.5 rounded-md text-xs bg-slate-800/50 border border-slate-700/50 text-slate-300 hover:text-white disabled:opacity-40 flex items-center gap-1">
                <ChevronLeft size={12} /> Prev
              </button>
              <button
                onClick={() => setOffset(offset + PAGE_SIZE)}
                disabled={!hasNext || q.isFetching}
                className="px-3 py-2.5 md:px-2 md:py-1.5 rounded-md text-xs bg-slate-800/50 border border-slate-700/50 text-slate-300 hover:text-white disabled:opacity-40 flex items-center gap-1">
                Next <ChevronRight size={12} />
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
